import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Animated, View } from 'react-native';

import { millisecondsToSeconds } from 'date-fns';
import { Subscription } from 'rxjs';

import { ContentType } from 'services/data-manager/types';

import ticker$ from 'utils/hooks/useInterval';

import {
    RibbonTouchableType,
    collectionContainerDimensions,
    metadataTypeToRibbonType,
    stripeContainerDimensions,
} from 'components/CustomButtons/RibbonTouchable/styles';

import styles, { progressBarMargin } from './styles';

type Props = {
    start?: number;
    end?: number;
    contentType?: ContentType;
    isCollection?: boolean;
    backgroundColor?: string;
    visibleItems: Record<string, boolean> | null;
    itemId: string | null;
    barWidth?: number;
};

/**
 * Progress bar component for "Program-like" RibbonItem.
 */
function ProgressBar({
    start,
    end,
    contentType,
    isCollection,
    backgroundColor,
    visibleItems,
    itemId,
    barWidth,
}: Props) {
    const width = useMemo(() => {
        if (barWidth) {
            return barWidth;
        }

        const ribbonType =
            (contentType && metadataTypeToRibbonType[contentType]) ||
            RibbonTouchableType.LANDSCAPE;
        const ribbonStyle = isCollection
            ? collectionContainerDimensions[ribbonType]
            : stripeContainerDimensions[ribbonType];
        return typeof ribbonStyle?.width === 'number'
            ? ribbonStyle?.width - progressBarMargin * 2
            : undefined;
    }, [contentType, isCollection, barWidth]);

    const animProgress = useRef(
        new Animated.Value(getInitialProgress(start, end)),
    ).current;
    const progressOffset = useRef(
        new Animated.Value(
            getLeftMargin(getInitialProgress(start, end), width),
        ),
    ).current;

    const ticker = useRef<Subscription | null>(null);

    const handleProgressUpdate = useCallback(() => {
        if (visibleItems != null && itemId != null && visibleItems[itemId]) {
            const newProgress = calculateProgress(start, end);

            if (newProgress > 0 && newProgress <= 1) {
                animProgress.setValue(newProgress);
                progressOffset.setValue(getLeftMargin(newProgress, width));
            }
        }
    }, [isCollection, start, end, width, visibleItems, itemId]);

    useEffect(() => {
        const initialProgress = getInitialProgress(start, end);

        if (initialProgress >= 0 && initialProgress <= 1) {
            if (ticker.current === null) {
                ticker.current = ticker$(1000).subscribe(handleProgressUpdate);
            }
        } else if (ticker.current) {
            ticker.current.unsubscribe();
        }

        return () => {
            if (ticker.current) {
                ticker.current.unsubscribe();
                ticker.current = null;
            }
        };
    }, [start, end, handleProgressUpdate]);

    return (
        <View style={styles.root}>
            <View style={styles.container}>
                {width ? (
                    <Animated.View
                        style={[
                            styles.progress,
                            {
                                backgroundColor,
                                transform: [
                                    {
                                        scaleX: animProgress,
                                    },
                                ],
                                marginLeft: progressOffset,
                            },
                        ]}
                    />
                ) : null}
            </View>
        </View>
    );
}

export default ProgressBar;

/**
 * Get progress value based on start and end time in seconds.
 * @param start
 * @param end
 */
function calculateProgress(start?: number, end?: number) {
    const now = millisecondsToSeconds(Date.now());

    return start && end ? (now - start) / (end - start) : 0;
}

/**
 * Get initial progress value between 0 and 1.
 * @param start
 * @param end
 */
function getInitialProgress(start?: number, end?: number) {
    const progress = calculateProgress(start, end);

    if (progress > 1) {
        return 1;
    }

    if (progress < 0) {
        return 0;
    }

    return progress;
}

function getLeftMargin(scale: number, width?: number) {
    if (!width) {
        return 0;
    }

    return -((width - width * scale) / 2);
}
